import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  CaptionsIcon,
  FullscreenIcon,
  Menu,
  MenuItem,
  PauseIcon,
  PictureInPictureIcon,
  PlayIcon,
  SettingsIcon,
  TheaterIcon,
  VolumeIcon,
  VolumeMuteIcon,
  cx,
} from '@/shared/ui';
import { formatTimecode } from '@/shared/utils/format';

export type PlayerControlsProps = {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** The element to throw into fullscreen — the stage, not the bare video, so
      the controls go with it. */
  stageRef: RefObject<HTMLDivElement | null>;
  /** Whether a caption track was attached; the CC button hides without one. */
  hasCaptions: boolean;
  theater: boolean;
  onToggleTheater: () => void;
};

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * The player's own control bar, replacing the browser's.
 *
 * The Content Page frames draw this: play, volume and the clock on the left, and
 * captions / settings / theater / fullscreen on the right, over the foot of the
 * video. Native controls were standing in — accessible and free, but a different
 * shape in every browser and conspicuously unlike everything around them. Six
 * icons had been drawn for this bar and were sitting unused in `icons.tsx`.
 *
 * State is read *from* the video element rather than pushed to it: every control
 * calls a method and then waits for the corresponding event to come back. That
 * way the bar still tells the truth when something else drives playback — the
 * transcript seeking, a keyboard shortcut, or the OS media keys.
 */
export function PlayerControls({
  videoRef,
  stageRef,
  hasCaptions,
  theater,
  onToggleTheater,
}: PlayerControlsProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  /* The element's own level, 0–1. Mute is a separate flag on the element and
     leaves this untouched, so the slider reads `muted ? 0 : volume` and
     unmuting returns the track to where it was rather than to full. */
  const [volume, setVolume] = useState(1);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [rate, setRate] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  /* Picture-in-picture is not universal: Safari has its own API under a
     different name and Firefox draws its own control rather than exposing this
     one. A button that throws is worse than no button, so it is rendered only
     where the standard API is actually available. */
  const [pipAvailable, setPipAvailable] = useState(false);
  const [pip, setPip] = useState(false);
  /* While the range input is being dragged, its value leads and `timeupdate`
     must not fight it back to where the video still is. */
  const scrubbing = useRef(false);
  /* The drag's destination, seeked once on release. A range input fires
     `change` on every pointer move, and seeking per move sets `currentTime`
     dozens of times across one drag — each a fresh HTTP range request and
     decoder flush on a streamed mp4. During a drag the bar and clock preview
     the destination (setCurrent above them); only the release commits it.
     Keyboard steps arrive with no pointer down (`scrubbing` false) and still
     seek immediately. */
  const pendingSeek = useRef<number | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const syncPlay = () => setPlaying(!video.paused && !video.ended);
    const syncTime = () => {
      if (!scrubbing.current) setCurrent(video.currentTime);
    };
    const syncMeta = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const syncVolume = () => {
      setMuted(video.muted || video.volume === 0);
      setVolume(video.volume);
    };
    const syncRate = () => setRate(video.playbackRate);

    syncPlay();
    syncMeta();
    syncVolume();
    syncRate();

    video.addEventListener('play', syncPlay);
    video.addEventListener('pause', syncPlay);
    video.addEventListener('ended', syncPlay);
    video.addEventListener('timeupdate', syncTime);
    video.addEventListener('loadedmetadata', syncMeta);
    video.addEventListener('durationchange', syncMeta);
    video.addEventListener('volumechange', syncVolume);
    video.addEventListener('ratechange', syncRate);
    return () => {
      video.removeEventListener('play', syncPlay);
      video.removeEventListener('pause', syncPlay);
      video.removeEventListener('ended', syncPlay);
      video.removeEventListener('timeupdate', syncTime);
      video.removeEventListener('loadedmetadata', syncMeta);
      video.removeEventListener('durationchange', syncMeta);
      video.removeEventListener('volumechange', syncVolume);
      video.removeEventListener('ratechange', syncRate);
    };
  }, [videoRef]);

  /* The track is attached by ContentStage and the browser may already be showing
     it, so the button has to read the track rather than assume it starts off —
     otherwise CC reports "off" over a video with visible captions, and the first
     press appears to do nothing because it turns them off.
     Reading it once was not enough, and the reason is ordering: ContentPage is
     what defaults captions on, and a parent's effects run *after* its children's.
     So this read the mode before it was set, every time. `cuechange` did not
     rescue it either — that fires when the active cue changes, not when the mode
     does, so the button stayed wrong until playback crossed a cue boundary.
     `change` on the TextTrackList is the event that actually means "a track's
     mode changed", which is the thing this button draws. */
  useEffect(() => {
    const tracks = videoRef.current?.textTracks;
    if (!tracks) return;
    const sync = () => setCaptionsOn(tracks[0]?.mode === 'showing');
    sync();
    tracks.addEventListener?.('change', sync);
    return () => tracks.removeEventListener?.('change', sync);
  }, [videoRef, hasCaptions]);

  /* Fullscreen can be left by pressing Escape, which fires no click of ours —
     so the button's state comes from the document, not from our own handler. */
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [stageRef]);

  /* Same reasoning as fullscreen, and more so: the floating window has its own
     close button, and the browser closes it on navigation. Neither is a click
     of ours, so the state has to come from the element's events. */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setPipAvailable(document.pictureInPictureEnabled === true && !video.disablePictureInPicture);

    const enter = () => setPip(true);
    const leave = () => setPip(false);
    video.addEventListener('enterpictureinpicture', enter);
    video.addEventListener('leavepictureinpicture', leave);
    return () => {
      video.removeEventListener('enterpictureinpicture', enter);
      video.removeEventListener('leavepictureinpicture', leave);
    };
  }, [videoRef]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    // `play()` rejects when the browser blocks autoplay or the source is gone;
    // unhandled it surfaces as an console error the user cannot act on.
    if (video.paused) void video.play().catch(() => undefined);
    else video.pause();
  }, [videoRef]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !video.muted;
    video.muted = next;
    /* Unmuting a track that was dragged to zero is silent, which reads as a
       dead button. Give it something audible to come back to. */
    if (!next && video.volume === 0) video.volume = 0.5;
  };

  /* The one control here that writes state before the element answers, and for
     the same reason the scrub bar does: this is a controlled input, and
     `volumechange` is queued rather than synchronous, so waiting for it lets the
     thumb spring back under the pointer between the move and the event. The
     event still arrives and still reconciles — it simply agrees. */
  const changeVolume = (next: number) => {
    setVolume(next);
    setMuted(next === 0);
    const video = videoRef.current;
    if (!video) return;
    video.volume = next;
    // Dragging up out of silence is an unmute; otherwise the slider moves and
    // nothing is heard.
    video.muted = next === 0;
  };

  const togglePip = () => {
    const video = videoRef.current;
    if (!video) return;
    if (document.pictureInPictureElement === video) {
      void document.exitPictureInPicture().catch(() => undefined);
    } else {
      void video.requestPictureInPicture?.().catch(() => undefined);
    }
  };

  const toggleCaptions = () => {
    const video = videoRef.current;
    const track = video?.textTracks?.[0];
    if (!track) return;
    const next = track.mode === 'showing' ? 'hidden' : 'showing';
    track.mode = next;
    setCaptionsOn(next === 'showing');
  };

  const toggleFullscreen = () => {
    const stage = stageRef.current;
    if (!stage) return;
    if (document.fullscreenElement === stage) void document.exitFullscreen().catch(() => undefined);
    else void stage.requestFullscreen?.().catch(() => undefined);
  };

  const seek = (value: number) => {
    const video = videoRef.current;
    if (video) video.currentTime = value;
  };

  const endScrub = () => {
    scrubbing.current = false;
    if (pendingSeek.current !== null) {
      seek(pendingSeek.current);
      pendingSeek.current = null;
    }
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  /* `is-paused` keeps the bar up while the video is stopped. The stylesheet
     used to ask the element itself, with `:has(video:paused)`, which Chrome
     cannot parse — so it dropped the rule and a paused video sat there with no
     controls at all. We already know whether it is playing; say so. */
  return (
    <div className={cx('player', !playing && 'is-paused')} data-testid="player-controls">
      <label className="player__scrub">
        <span className="sr-only">Seek</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(current, duration || 0)}
          // A range input is the whole reason this is keyboard-operable for
          // free: arrows step, Home and End jump the ends.
          onChange={(event) => {
            const next = Number(event.target.value);
            setCurrent(next);
            if (scrubbing.current) pendingSeek.current = next;
            else seek(next);
          }}
          onPointerDown={() => {
            scrubbing.current = true;
          }}
          /* All three, because only one of them is guaranteed to arrive: a
             pointer can be cancelled (a touch drag reinterpreted as a scroll, an
             OS-level interruption) and then `pointerup` never fires. The flag
             would stay latched, `syncTime` would go on short-circuiting, and the
             seek bar and clock would sit frozen while the video played on — for
             the rest of the session, since nothing else clears it. Whichever
             arrives first commits the pending seek; the others find it null. */
          onPointerUp={endScrub}
          onPointerCancel={endScrub}
          onLostPointerCapture={endScrub}
          aria-label="Seek"
          aria-valuetext={`${formatTimecode(current)} of ${formatTimecode(duration)}`}
          style={{ '--player-progress': `${progress}%` } as React.CSSProperties}
        />
      </label>

      <div className="player__bar">
        <button
          type="button"
          className="player__btn player__btn--lead"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
        </button>

        <button
          type="button"
          className="player__btn"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
          aria-pressed={muted}
        >
          {muted ? <VolumeMuteIcon size={18} /> : <VolumeIcon size={18} />}
        </button>

        {/* The level, not just on/off. Replacing the browser's controls took the
            slider with them, and mute is not a way to turn something down. */}
        <label className="player__volume">
          <span className="sr-only">Volume</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={muted ? 0 : volume}
            onChange={(event) => changeVolume(Number(event.target.value))}
            aria-label="Volume"
            aria-valuetext={`${Math.round((muted ? 0 : volume) * 100)}%`}
            style={
              { '--player-volume': `${(muted ? 0 : volume) * 100}%` } as React.CSSProperties
            }
          />
        </label>

        <span className="player__clock">
          {formatTimecode(current)} / {formatTimecode(duration)}
        </span>

        <div className="player__end">
          {hasCaptions ? (
            <button
              type="button"
              className={cx('player__btn', captionsOn && 'is-on')}
              onClick={toggleCaptions}
              aria-label="Captions"
              aria-pressed={captionsOn}
            >
              <CaptionsIcon size={18} />
            </button>
          ) : null}

          <Menu
            label="Playback speed"
            placement="top-end"
            trigger={
              <button type="button" className="player__btn" aria-label="Playback speed">
                <SettingsIcon size={18} />
              </button>
            }
          >
            {RATES.map((value) => (
              <MenuItem
                key={value}
                onSelect={() => {
                  const video = videoRef.current;
                  if (video) video.playbackRate = value;
                }}
                selected={rate === value}
              >
                {value === 1 ? 'Normal' : `${value}×`}
              </MenuItem>
            ))}
          </Menu>

          {pipAvailable ? (
            <button
              type="button"
              className={cx('player__btn', pip && 'is-on')}
              onClick={togglePip}
              aria-label={pip ? 'Exit picture in picture' : 'Picture in picture'}
              aria-pressed={pip}
            >
              <PictureInPictureIcon size={18} />
            </button>
          ) : null}

          <button
            type="button"
            className={cx('player__btn', theater && 'is-on')}
            onClick={onToggleTheater}
            aria-label="Theater mode"
            aria-pressed={theater}
          >
            <TheaterIcon size={18} />
          </button>

          <button
            type="button"
            className={cx('player__btn', fullscreen && 'is-on')}
            onClick={toggleFullscreen}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            aria-pressed={fullscreen}
          >
            <FullscreenIcon size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
